'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';

export function useNotificationWS() {
  const { user } = useAuthStore();
  const { addNotification, fetchUnreadCount, fetchNotifications } = useNotificationStore();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsFailCount = useRef(0);

  // 폴링 모드 (WebSocket 실패 시 대체)
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      fetchUnreadCount();
      fetchNotifications();
    }, 15000); // 15초마다 폴링
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const connect = useCallback(() => {
    if (!user) return;

    const token = typeof window !== 'undefined' ? localStorage.getItem('wbs_auth_token') : null;
    if (!token) return;

    // WebSocket 3회 실패 시 폴링으로 전환
    if (wsFailCount.current >= 3) {
      startPolling();
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        wsFailCount.current = 0;
        stopPolling();
        fetchUnreadCount();
        fetchNotifications();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && data.notification) {
            addNotification(data.notification);
          }
        } catch { /* */ }
      };

      ws.onclose = () => {
        wsRef.current = null;
        wsFailCount.current++;
        reconnectRef.current = setTimeout(() => connect(), 5000);
      };

      ws.onerror = () => {
        ws.close();
      };

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      ws.addEventListener('close', () => clearInterval(pingInterval));
    } catch {
      wsFailCount.current++;
      startPolling();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, addNotification, fetchUnreadCount, fetchNotifications, startPolling]);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchNotifications();
      connect();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      stopPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return wsRef;
}
